import React, { useState, useEffect, useMemo, useCallback } from 'react';
import cx from 'classnames';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';

import Icon from 'components/Icon';
import Button2 from 'components/Button2';
import MenuItem from './MenuItem';
import styles from './index.module.scss';

import useMobile from 'hooks/useMobile.hook';

export const height = {
  desktop: 80,
  mobile: 108,
};

const configMap = ({ company, enterprise, product }) => [
  {
    id: 'PRODUCT',
    title: 'Product',
    subcategories: product,
  },
  {
    id: 'ENTERPRICE',
    title: 'Enterprice',
    subcategories: enterprise,
  },
  {
    id: 'PRICING',
    title: 'Pricing',
    to: '/pricing',
    subcategories: null,
  },
  {
    id: 'COMPANY',
    title: 'Company',
    subcategories: company,
  },
  {
    id: 'SUPPORT',
    title: 'Support',
    to: '/help-center',
    subcategories: null,
  },
];

const Navigation = ({ active, setActive, homePage, config }) => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const isMobile = useMobile();
  const history = useHistory();

  const menuMap = useMemo(() => configMap(config), [config]);

  useEffect(() => {
    if (active) {
      setMounted(true);
      setOpen(true);
      setHidden(true);
    } else {
      setOpen(false);
      setMounted(false);
      setTimeout(() => {
        setHidden(false);
      }, 300);
    }
  }, [active, isMobile, menuMap]);

  const handleRowClick = useCallback(
    (item) => () => {
      if (item.to) {
        history.push(item.to);
        setOpen(false);
        setMounted(false);
        setHidden(false);
        return;
      }
      if (isMobile) {
        setCurrentCategory(currentCategory ? null : item.id);
      } else {
        setCurrentCategory(item.id);
      }
    },
    [currentCategory, history, isMobile],
  );

  return (
    <div
      className={cx(
        styles.navigation,
        mounted && styles.opacity,
        hidden && styles.visible,
      )}
    >
      <Drawer
        anchor="left"
        open={open}
        variant="persistent"
        className={styles.drawer}
      >
        <div className={styles.drawerWrapper}>
          <div className={styles.header}>
            <Box mt={2} pb={2} className={styles.action}>
              <div className={styles.actions}>
                <Button2
                  variant="contained"
                  color="primary"
                  to="/sign-in"
                  component={Link}
                >
                  Sign in
                </Button2>
                <Button2
                  variant="outlined"
                  color="primary"
                  to="/sign-up"
                  component={Link}
                >
                  Create an Account
                </Button2>
              </div>
            </Box>
            <Box className={styles.titleContainer}>
              <IconButton
                size={isMobile ? 'small' : undefined}
                className={styles.close}
                onClick={() => {
                  setActive(false);
                }}
              >
                <Icon.Close />
              </IconButton>
            </Box>
          </div>
          <List component="nav" className={styles.nav}>
            {menuMap
              .filter(
                (cat) =>
                  cat.title !== 'Services' &&
                  cat.title !== 'Electronic Components',
              )
              .map((item) => {
                return (
                  <Collapse
                    key={item.id}
                    in={
                      !isMobile ||
                      item.id === currentCategory ||
                      !currentCategory
                    }
                  >
                    <ListItem
                      disableGutters
                      className={cx(
                        styles.navItem,
                        item.id === currentCategory && styles.current,
                      )}
                    >
                      <Hidden smUp>
                        {item.subcategories && (
                          <ListItemIcon className={styles.iconBackContainer}>
                            <IconButton
                              size="small"
                              className={styles.iconLeft}
                              onClick={() => setCurrentCategory(null)}
                            >
                              <Icon.ArrowAngular />
                            </IconButton>
                          </ListItemIcon>
                        )}
                      </Hidden>

                      <ListItemText
                        disableTypography
                        className={styles.navItemText}
                        primary={item.title}
                        onClick={handleRowClick(item)}
                      />
                      {item.subcategories && (
                        <ListItemIcon
                          className={styles.navItemIconAfter}
                          onClick={() => {
                            if (isMobile) {
                              setCurrentCategory(
                                currentCategory ? null : item.id,
                              );
                            } else {
                              setCurrentCategory(item.id);
                            }
                          }}
                        >
                          <Icon.ArrowAngular />
                        </ListItemIcon>
                      )}
                    </ListItem>
                  </Collapse>
                );
              })}
          </List>
          {currentCategory !== null && (
            <MenuItem
              title=""
              submenu={
                menuMap.find(({ id }) => id === currentCategory).subcategories
              }
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default React.memo(Navigation);
